import React, { useContext, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, FlatList, TouchableOpacity } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { PaymentSummaryContext } from '../../context/PaymentSummaryContext';
import { useAppSelector } from '@/store';
import { selectUser, isUserLeaseHolder, selectLeaseIds } from '@/store/selectors';
import { useLeaseHistory, useSortableRows, SortField, SortOrder, HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';

// capitalizeFirstLetter now provided by shared utils

interface Props {
  heading: string;
  noDataView: React.ReactNode;
  alwaysShowNoData?: boolean;
  hasMoreThanOneConnections?: boolean;
}

// Types now imported from hooks

export const HistoryTable: React.FC<Props> = ({ heading, noDataView, alwaysShowNoData, hasMoreThanOneConnections }) => {
  const { currentlyDisplayedLeaseId } = useContext(PaymentSummaryContext);
  const user = useAppSelector(selectUser);
  const isLeaseHolder = useAppSelector(isUserLeaseHolder);
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const { rows, hasError, isFetching } = useLeaseHistory(currentlyDisplayedLeaseId, user?.id);
  
  // Sorting state (matching web app defaults)
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const sortedRows: HistoryRow[] = useSortableRows(rows, sortField, sortOrder);

  const currentLeaseIsOwn = useMemo(
    () => ownLeaseIds.includes(currentlyDisplayedLeaseId),
    [ownLeaseIds, currentlyDisplayedLeaseId]
  );

  // Sorting handled by hook

  // Handle sort column click
  const handleSort = (field: SortField) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
  };

  // Data now loaded via hook and sorted by hook

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    
    const ChevronIcon = sortOrder === 'asc' 
      ? RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT // Use as up arrow
      : RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT; // Use as down arrow
    
    return (
      <View style={[styles.sortIcon, sortOrder === 'desc' && styles.sortIconDown]}>
        <ChevronIcon width={12} height={12} color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY} />
      </View>
    );
  };

  // Table header component with sorting
  const renderTableHeader = () => (
    <View style={styles.tableHeader}>
      <TouchableOpacity style={[styles.headerCell, styles.headerCellDescription]} onPress={() => handleSort('description')}>
        <Text style={styles.headerText}>DESCRIPTION</Text>
        <SortIndicator field="description" />
      </TouchableOpacity>
      
      {hasMoreThanOneConnections && (
        <TouchableOpacity style={[styles.headerCell, styles.headerCellLinkedAccounts]} onPress={() => handleSort('recipient')}>
          <Text style={styles.headerText}>LINKED ACCOUNTS</Text>
          <SortIndicator field="recipient" />
        </TouchableOpacity>
      )}
      
      <TouchableOpacity style={[styles.headerCell, styles.headerCellPosted]} onPress={() => handleSort('date')}>
        <Text style={styles.headerText}>POSTED</Text>
        <SortIndicator field="date" />
      </TouchableOpacity>
      
      <TouchableOpacity style={[styles.headerCell, styles.headerCellAmount]} onPress={() => handleSort('amount')}>
        <Text style={styles.headerText}>AMOUNT</Text>
        <SortIndicator field="amount" />
      </TouchableOpacity>
    </View>
  );

  // Table row component matching web app
  const renderTableRow = ({ item }: { item: HistoryRow }) => (
    <View style={styles.tableRow}>
      <Text style={[styles.cellText, styles.cellDescription]} numberOfLines={2}>
        {item.description}
      </Text>
      {hasMoreThanOneConnections && (
        <Text style={[styles.cellText, styles.cellLinkedAccounts]} numberOfLines={1}>
          {item.recipient || ''}
        </Text>
      )}
      <Text style={[styles.cellText, styles.cellPosted]}>
        {formatShortDate(item.date)}
      </Text>
      <Text style={[styles.cellText, styles.cellAmount, item.type === 'payment' ? styles.amountPayment : undefined]}>
        {formatCurrency(item.amount)}
      </Text>
    </View>
  );

  if (!currentlyDisplayedLeaseId) return null;

  return (
    <View style={styles.container}>
      <Text style={styles.heading}>{heading}</Text>

      {alwaysShowNoData ? (
        <View style={styles.noDataWrap}>{noDataView}</View>
      ) : hasError ? (
        <View style={styles.noDataWrap}>
          <Text style={styles.noDataText}>Error loading transaction history. Please try again.</Text>
        </View>
      ) : isFetching ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator />
        </View>
      ) : sortedRows.length === 0 ? (
        <View style={styles.noDataWrap}>
          <Text style={styles.noDataText}>No transactions yet.</Text>
        </View>
      ) : (
        <View style={styles.tableContainer}>
          {renderTableHeader()}
          <FlatList
            data={sortedRows}
            keyExtractor={(item) => item.id}
            renderItem={renderTableRow}
            ItemSeparatorComponent={() => <View style={styles.tableSeparator} />}
            showsVerticalScrollIndicator={false}
          />
        </View>
      )}
    </View>
  );
};

// formatShortDate provided by shared utils

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    marginHorizontal: 8,
  },
  heading: {
    fontSize: 20,
    fontWeight: '800',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  loadingWrap: {
    paddingVertical: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noDataWrap: {
    paddingVertical: 12,
  },
  noDataText: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  // Table styles matching web app
  tableContainer: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  headerCell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  headerCellDescription: {
    flex: 2, // 2/4 = 50% width
  },
  headerCellLinkedAccounts: {
    flex: 1, // 1/4 = 25% width
  },
  headerCellPosted: {
    flex: 1, // 1/4 = 25% width
  },
  headerCellAmount: {
    flex: 1, // 1/4 = 25% width
  },
  headerText: {
    fontSize: 12,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    textTransform: 'uppercase',
  },
  sortIcon: {
    marginLeft: 4,
    transform: [{ rotate: '-90deg' }], // Point up for asc
  },
  sortIconDown: {
    transform: [{ rotate: '90deg' }], // Point down for desc
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  cellText: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  cellDescription: {
    flex: 2, // 2/4 = 50% width
  },
  cellLinkedAccounts: {
    flex: 1, // 1/4 = 25% width
  },
  cellPosted: {
    flex: 1, // 1/4 = 25% width
  },
  cellAmount: {
    flex: 1, // 1/4 = 25% width
    textAlign: 'left',
    fontWeight: '600',
  },
  amountPayment: {
    color: '#059669', // Green for payments
  },
  tableSeparator: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.06)',
    marginHorizontal: 16,
  },
});


